import { z } from 'zod'
import { ContractCurrency, ContractFormat, ContractRole } from './contract.js'
import { CompartimentId, CriterionId, DefaultFieldId, DefaultFieldKey, FieldId } from './id.js'
import { Text } from './text.js'

/**
 * De veldcatalogus (B15, ontwerp 6.2): de 26 controltypes uit eisenboek 8.2,
 * plus vier eigen vormen. Dat zijn de waarden die `control` in een definitie kan
 * aannemen.
 *
 * Drie dingen die hier met opzet niet staan, omdat ze regels zijn en geen
 * veldeigenschappen. Ze hier ook neerzetten zou een tweede bron voor hetzelfde
 * begrip opleveren (ontwerp 11.4):
 *
 * - De teken- en woordlimiet van een tekstveld. Dat is `maximumTekens` of
 *   `maximumWoorden`; de teller van R-12 en R-13 leest die regel.
 * - De bestandseisen van een bijlageveld. Dat is `bestandseisen` (R-104).
 * - Het bereik van een `slider` of `number`. Dat is `getalsbereik`.
 *
 * En één ding dat er niet staat terwijl het er op het eerste gezicht hoort:
 * **geen `verplicht`-vlag op een veld.** R-09 noemt een verplicht-aanduiding en
 * R-26 zegt dat verplicht een regelsoort is en niet een vlag zonder gedrag. Als
 * beide zouden bestaan, kunnen ze uit elkaar lopen. De aanduiding die de
 * invuller ziet volgt dus uit het bestaan van een `verplicht`-regel op dat veld,
 * en de renderer leidt hem af (M2).
 */

/**
 * De 26 controltypes uit eisenboek 8.2: de 27 van de PoC minus map en iframe (T6), plus `textarea`
 * (besluit van 27 oktober 2026). Die laatste komt uit de CitatenPOC, die naast tekst en rich text een
 * meerregelig veld zonder opmaak kent (ontwerp bijlage C); 12.9 legt de set controls niet vast.
 */
export const CATALOG = [
  'text',
  'textarea',
  'number',
  'currency',
  'url',
  'email',
  'phone',
  'richtext',
  'dropdown',
  'multiselect',
  'toggle',
  'checkbox',
  'radio',
  'date',
  'datetime',
  'lookup',
  'tag',
  'status',
  'notification',
  'rating',
  'slider',
  'progress',
  'file',
  'image',
  'timer',
  'subgrid',
] as const

/**
 * Vijf eigen vormen naast de catalogus (ontwerp 6.2). `akkoordPerPersoon` kwam erbij met het besluit
 * van 3 november 2026 (eisenboek 12.56): een akkoord dat elke invuller voor zichzelf geeft.
 */
export const OWN_SHAPES = ['criterium', 'herhaalgroep', 'verwijzing', 'component', 'akkoordPerPersoon'] as const

export const CONTROLS = [...CATALOG, ...OWN_SHAPES] as const
export type Control = (typeof CONTROLS)[number]

/**
 * De controls die vrije tekst dragen, en dus een opmerking kunnen zijn (R-75).
 *
 * Drie: `richtext` voor een opmerking met opmaak, `text` voor een korte regel en `textarea` voor
 * meerdere regels zonder opmaak. Die laatste is voor een teruglegopmerking de meest gewone vorm: een
 * toetser schrijft een paar zinnen of een opsomming, en opmaak heeft hij daar niet voor nodig. Wat hier
 * niet in staat kan geen teruglegopmerking zijn, en dat is de controle in `definitie.ts`. Dezelfde
 * lijst zegt welke controls een teken- of woordlimiet dragen (R-12, D-11): een veld dat tekst draagt
 * kan een grens hebben.
 */
export const TEXT_CONTROLS = ['text', 'textarea', 'richtext'] as const

/**
 * De controls die een veld van het processysteem kunnen tonen of aanvullen (`DossierField`): een
 * enkele waarde met een type dat ook in Dataverse een kolom is. Geen bijlage, herhaalgroep,
 * meerkeuze, component, criterium of verwijzing.
 */
export const DOSSIER_CONTROLS = [
  'text',
  'textarea',
  'richtext',
  'number',
  'currency',
  'date',
  'datetime',
  'email',
  'url',
  'phone',
  'dropdown',
  'radio',
  'toggle',
] as const

/**
 * De vaste componenten uit B14. Vast gebouwd, als geheel plaatsbaar met
 * beperkte instellingen (R-46, T4), niet door de inrichter samen te stellen.
 * De laatste drie hebben een gereserveerde interface; hun binnenkant volgt na de
 * U10-sessie met NWO.
 */
export const COMPONENTS = [
  'profiel',
  'projectbetrokkenen',
  'publicatielijst',
  'referentielijst',
  'outputRegistreren',
  'sjabloon',
  'begroting',
  'cofinanciering',
  'lening',
] as const
export type Component = (typeof COMPONENTS)[number]

/** Een keuzeoptie draagt zijn label per taal (R-143). */
const Option = z.strictObject({ waarde: z.string().min(1), label: Text })

const Options = z
  .array(Option)
  .min(1)
  .refine((o) => new Set(o.map((x) => x.waarde)).size === o.length, {
    error: 'twee keuzeopties met dezelfde waarde',
  })

/**
 * De oordeelschaal van een criterium is instelbaar (R-148): een tweekeuze zoals
 * voldoet of voldoet niet, of een cijferbereik zoals 1 tot 9.
 *
 * Bij een cijferbereik mag per waarde een label staan. Dat is nodig omdat een
 * schaal met benoemde banden bestaat: het voorbeeldbeoordelingsformulier uit
 * extract 214 loopt van 1 tot 9 met Excellent, Zeer goed, Goed, Matig en Slecht
 * erboven. Zo'n label is een keuzeoptie en valt dus onder R-143.
 *
 * Er staat geen `richting` bij, en dat is opzet: in extract 214 is 1 de beste
 * score. Die richting zit al in de labels. Een apart veld ernaast zou een tweede
 * bron voor hetzelfde zijn en kan er tegenin gaan.
 */
export const Scale = z.discriminatedUnion('soort', [
  z.strictObject({
    soort: z.literal('tweekeuze'),
    /** Weglaten betekent: de standaard van de renderer, voldoet of voldoet niet. */
    opties: z.tuple([Option, Option]).optional(),
  }),
  z.strictObject({
    soort: z.literal('cijferbereik'),
    minimum: z.number().int(),
    maximum: z.number().int(),
    /** Optioneel een label per waarde, of per bandgrens zoals in extract 214. */
    waarden: z
      .array(z.strictObject({ waarde: z.number().int(), label: Text }))
      .min(1)
      .optional(),
  }),
])
export type Scale = z.infer<typeof Scale>

/**
 * Wat elk veld draagt.
 *
 * `label` staat hier niet, omdat het per controltype verschilt of het verplicht
 * is: een geplaatst component brengt zijn eigen presentatie mee (R-46) en heeft
 * er geen, zoals het voorbeeld in ontwerp 4.4 laat zien.
 */
/**
 * Waar een geplaatst veld uit is gekopieerd (D-52).
 *
 * Optioneel op het veld: een zelf toegevoegd veld heeft geen herkomst, en dat is de normale
 * situatie zolang de bibliotheek leeg is. Aanwezig betekent: dit veld is uit de bibliotheek
 * gekomen, en dan is het gebruik zichtbaar te maken.
 */
export const FieldOrigin = z.strictObject({
  standaardveld: DefaultFieldId,
  /** De versie waaruit is gekopieerd; zie `Standaardveld.versie` voor waarom die meemoet. */
  versie: z.number().int().min(1),
  /**
   * De vaste sleutel van het standaardveld (besluit van 1 november 2026), zodat over formulieren en
   * rondes heen te zien is dat twee velden dezelfde vraag zijn.
   *
   * Optioneel, want een merk van vóór die dag draagt hem niet en blijft geldig. Hij staat in de
   * definitie en gaat nog niet mee in het XSD of het bericht naar het processysteem: dat wacht op
   * afstemming met de Dataverse-kant (`docs/koppelvlak.md` noemt hem daarom niet).
   */
  sleutel: DefaultFieldKey.optional(),
})
export type FieldOrigin = z.infer<typeof FieldOrigin>

/** Wat elk veld draagt, plus waar het vandaan komt als het uit de bibliotheek kwam. */
const core = { id: FieldId, uitStandaardveld: FieldOrigin.optional() }

/**
 * Of dit veld naar de indiener teruggaat bij een teruglegging (R-75).
 *
 * **Configuratie en geen data.** R-75 zegt het letterlijk: het opmerkingveld is geen
 * impliciete voorziening maar iets wat bij het samenstellen wordt ingesteld. Het staat dus
 * op het veld in de definitie en niet als vlag op de waarde: zou het bij de waarde staan,
 * dan besluit de toetser per keer of zijn opmerking meereist, en dan is voor de invuller
 * niet te zeggen wat hij hoort te zien.
 *
 * Alleen in een toetssectie, en alleen op een veld dat tekst draagt. Beide worden in
 * `definitie.ts` afgedwongen, want een cijfer of een keuze dat "teruggaat" heeft geen
 * betekenis: wat de invuller bij zijn antwoord ziet is een opmerking en geen oordeel. Het
 * oordeel zelf reist langs zijn eigen weg (R-182, R-187).
 *
 * Weggelaten betekent nee. Dat is hier de veilige kant: een opmerking van de toetser die
 * ongevraagd naar de aanvrager gaat is niet terug te nemen.
 */
const goesBack = z.boolean().optional()

/**
 * Het compartiment in het contract van het eigen bouwblok waarin de waarde van dit veld
 * landt (D-69, D-70).
 *
 * **Optioneel in de vorm en verplicht in de samenhang.** Welke velden er een nodig
 * hebben verschilt per controltype en per sectietype, en dat past niet in een tak van
 * de unie: een verwijzing en een alleen-lezen tabel leveren niets aan het dossier, een
 * criterium levert zijn uitkomst in het gestructureerde deel, en een rijveld landt in
 * het compartiment van zijn herhaalgroep en niet apart. `definitie.ts` dwingt het daarom af
 * zoals het `gaatTerug` afdwingt: in de `superRefine`, met de reden erbij.
 *
 * Er is geen standaardcompartiment. Publiceren blokkeert bij een veld zonder keuze (D-27,
 * D-69), want wat in het gesloten compartiment staat is later niet meer beschikbaar en dat is
 * niet iets om per ongeluk te kiezen.
 */
const compartiment = CompartimentId.optional()

/**
 * Of dit veld ook voor externen zichtbaar is, naast alleen intern (R-170, D-54).
 *
 * **Geen synoniem voor het compartiment.** Het compartiment (D-69, D-70) zegt of een waarde de
 * bouwblokgrens over gaat; deze eigenschap zegt of het veld binnen dít formulier ook aan een
 * externe gebruiker wordt getoond. Een veld kan intern-only zijn en toch naar het beoordelen
 * moeten, en omgekeerd (ontwerp 8.11); dat is precies waarom het eisenboek waarschuwt de twee
 * niet tot één instelling samen te trekken (eisenboek regel 1043).
 *
 * Weggelaten betekent ja. Vrijwel elk veld is voor de aanvrager zichtbaar; intern-only is de
 * uitzondering die de inrichter expliciet aanwijst (M8.11), niet de norm die elk veld apart zou
 * moeten bevestigen.
 */
const visibleExternally = z.boolean().optional()

/**
 * De naam van een veld van het processysteem, zoals in het dossier-contract (`koppelvlak.md`
 * hoofdstuk 2 en 4). Een geldige XML-naam, want in het aanmaakverzoek is het een element.
 */
export const DossierFieldName = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_.-]{0,99}$/, 'verwacht een veldnaam van het processysteem, zoals nwo_einddatum')

/**
 * Een veld dat een veld van het processysteem op het dossier toont of aanvult (R-54, R-56, R-58, U11).
 *
 * Het processysteem geeft bij het uitzetten de waarde mee in het dossier-contract, en het formulier
 * begint ermee. `aanpasbaar` zegt of de invuller de waarde mag veranderen; zo niet, dan is het veld
 * alleen-lezen en herkenbaar als overgenomen (R-58). Wat de invuller veranderde, gaat met de
 * meegegeven waarde terug in het bericht, onder `<dossier>` en niet in een compartiment (R-202).
 *
 * Een eigenschap van een gewoon veld en geen eigen control: het type komt van het veld, en daar
 * toetst het dossier-contract tegen (`koppelvlak.md` hoofdstuk 4). Op welke velden het mag staat in
 * `DOSSIER_CONTROLS`, en de definitie bewaakt het (`definitie.ts`).
 */
export const DossierField = z.strictObject({
  naam: DossierFieldName,
  aanpasbaar: z.boolean(),
})
export type DossierField = z.infer<typeof DossierField>

/** Een gewoon veld: label verplicht, hulptekst optioneel en standaard zichtbaar (R-09). */
const labeled = {
  ...core,
  label: Text,
  hulptekst: Text.optional(),
  gaatTerug: goesBack,
  compartiment,
  externZichtbaar: visibleExternally,
  dossierveld: DossierField.optional(),
}

/**
 * De controltypes zonder eigen instellingen: alles zit in het label en in de
 * regels. Elk type staat er los, want een gediscrimineerde unie heeft per tak
 * één letterlijke waarde nodig; een lus over een lijst levert er één met een
 * unie in, en dan discrimineert Zod niet meer.
 */
function withoutSettings<C extends Control>(control: C) {
  return z.strictObject({ ...labeled, control: z.literal(control) })
}

const SIMPLE = [
  withoutSettings('text'),
  /** Meerdere regels tekst zonder opmaak; verder als `text` (besluit van 27 oktober 2026). */
  withoutSettings('textarea'),
  withoutSettings('number'),
  withoutSettings('currency'),
  withoutSettings('url'),
  withoutSettings('email'),
  withoutSettings('phone'),
  withoutSettings('richtext'),
  withoutSettings('date'),
  withoutSettings('datetime'),
  /** Waar een lookup naar wijst staat in geen eis; dat blijft open tot de control wordt gebouwd. */
  withoutSettings('lookup'),
  withoutSettings('tag'),
  withoutSettings('rating'),
  /** Score bij een beoordelingsvraag; het bereik komt uit een getalsbereik-regel. */
  withoutSettings('slider'),
  withoutSettings('file'),
  withoutSettings('image'),
  /** De vier hieronder horen bij de schil, configurator-categorie Weergave (B15). */
  withoutSettings('status'),
  withoutSettings('notification'),
  withoutSettings('progress'),
  withoutSettings('timer'),
] as const

/**
 * Waar een alleen-lezen veld zijn inhoud haalt: een pad in het contract van een
 * bouwblok dat een document op het dossier heeft achtergelaten (R-54, R-56, D-37).
 * Dat is een blok eerder in de keten, of het eigen blok voor de reeks eerdere
 * instanties; zie `referenceableBlocks` in de engine voor waarom gelijk mag (R-59, R-62).
 *
 * Het pad mag één waarde aanwijzen, maar ook een hele sectie of een lijst (R-55).
 * Dat laatste is wat een bronsectie nodig heeft om te aggregeren: de oordelen uit
 * meerdere eerdere beoordelingen onder elkaar (R-62) zijn één pad naar een reeks,
 * niet een veld per beoordeling. Dat de configurator voorlopig alleen velden
 * aanbiedt is een keuze daar en niet hier (B17).
 */
const Source = z.strictObject({
  contract: z.string().min(1),
  pad: z.string().min(1),
})

/**
 * De bron van een `subgrid`, met de rol per eigenschap van de reeks die het pad aanwijst (D-73,
 * R-62, R-137).
 *
 * **Bevroren bij publiceren, en nooit door de inrichter gezet.** De rol staat in het contract van
 * het bronblok (`rol` per eigenschap van een reekselement, `docs/koppelvlak.md` hoofdstuk 2,
 * besluit 2), en de pod heeft geen contract en geen registry. De publicatiepoort schrijft hem
 * daarom uit de registry van de ronde in de definitie die bevriest (`freezeSeriesRoles` in
 * `packages/engine/src/publicatieCheck.ts`); zo zit hij in het image, onder de definitiehash van
 * het controleverslag, en hoeft een draaiende pod hem nergens op te halen (B2, D-36). Het concept
 * kent de sleutel niet (`SourceDraft` in `draft.ts`): wat de inrichter kiest is het pad, en de rol
 * volgt uit het contract. Het contract van een blok in een gepubliceerde ronde wijzigt niet
 * (D-43), dus deze kopie kan niet verlopen.
 *
 * **Drie standen, en ze zeggen elk iets anders.** Ontbreekt `rollen`, dan is dit een publicatie
 * van vóór deze sleutel, of wijst het pad niet naar een reeks; de renderer doet dan wat hij altijd
 * deed (`seriesRolesFor` in de renderer). Een leeg object zegt dat het contract de reeks kent en
 * geen eigenschap een rol geeft: de platte tabel (D-73). Anders staat per eigenschap haar rol, en
 * een eigenschap zonder rol staat er niet in en doet niet mee in de groepering.
 *
 * Alleen op een `subgrid`: een `verwijzing` leest één waarde en groepeert niets.
 */
const SeriesSource = Source.extend({
  rollen: z.record(z.string().min(1), ContractRole).optional(),
})

/**
 * De alleen-lezen tabel (R-08). In een gewone sectie is het een weergave van
 * eigen inhoud; in een bronsectie draagt hij een bron en toont hij een reeks uit
 * het dossier, bijvoorbeeld de eerdere beoordelingen van deze aanvraag.
 */
const SUBGRID = [
  z.strictObject({ ...labeled, control: z.literal('subgrid'), bron: SeriesSource.optional() }),
] as const

/**
 * De toggle, met het akkoordveld als variant (R-19, R-89, ontwerp 6.2).
 *
 * `akkoord` is geen apart controltype: 6.2 noemt het een toggle-variant met
 * systeemgedrag, en de catalogus van 8.2 kent het niet als eigen type. Staat de
 * vlag aan, dan stempelt de runtime bij Ja de naam van de ingelogde gebruiker en
 * het moment, beide niet bewerkbaar, en maakt ze bij Nee weer leeg (M2 en M3).
 * De identiteit komt daarbij uit het gastheer-token (R-154), nooit uit invoer.
 *
 * De poort op indienen is iets anders en staat los: dat is de regelsoort
 * `akkoordJa` (B11). Een akkoordveld zonder die regel is een akkoordveld dat niet
 * verplicht is.
 */
const TOGGLE = [
  z.strictObject({ ...labeled, control: z.literal('toggle'), akkoord: z.boolean().optional() }),
] as const

/** De keuzevelden. Een checkbox zonder opties is één losse instemming, met opties een aanvinklijst. */
const CHOICE = [
  z.strictObject({ ...labeled, control: z.literal('dropdown'), opties: Options }),
  z.strictObject({ ...labeled, control: z.literal('multiselect'), opties: Options }),
  z.strictObject({ ...labeled, control: z.literal('radio'), opties: Options }),
  z.strictObject({ ...labeled, control: z.literal('checkbox'), opties: Options.optional() }),
] as const

/** De 26 catalogustypen. Dit is ook wat een rij van een herhaalgroep mag bevatten. */
export const RowField = z.discriminatedUnion('control', [...SIMPLE, ...TOGGLE, ...SUBGRID, ...CHOICE])
export type RowField = z.infer<typeof RowField>

const criterion = z.strictObject({
  ...labeled,
  control: z.literal('criterium'),
  /** Domein-identiteit, buiten het formulier bruikbaar voor weging en aggregatie (R-150). */
  criteriumId: CriterionId,
  /**
   * De vraag waarover dit criterium een uitspraak doet, of niets (D-55).
   *
   * **De sectie of een veld, en dat zijn twee verschillende fijnheden.** Een toetssectie wijst
   * met `hoortBij` de sectie aan die zij toetst (R-109, D-05), en dat is de grove maat: elk
   * criterium in die sectie gaat dan over die sectie. D-55 zegt dat een criterium ook aan één
   * veld kan hangen, en dat is wat hier staat. Zonder deze schakel wijst een openstaand
   * criterium in de werkverzameling zijn hele sectie aan in plaats van de vraag waar het aan
   * schortte (`werkverzameling.ts` schreef dat als gemis op), en moet de invuller zelf zoeken
   * welke van de tien vragen bedoeld is.
   *
   * **Optioneel, want de sectie is de geldige grovere vorm.** Een criterium zonder dit veld is
   * geen halve configuratie: het gaat dan over de sectie die zijn toetssectie aanwijst. Dat is
   * precies wat de twee voorbeeldformulieren doen.
   *
   * Het veld hoort in dezelfde definitie te bestaan; `Definitie` toetst dat, want alleen daar is
   * de hele boom bekend.
   */
  hoortBijVeld: FieldId.optional(),
  schaal: Scale,
  /**
   * De onderbouwing is altijd verplicht (R-147); dat is geen instelling. Wat hier
   * kan staan is alleen een eigen label, in plaats van dat van de control.
   */
  onderbouwing: z.strictObject({ label: Text }).optional(),
  /**
   * Welke automatische controle haar uitkomst bij dit criterium aanlevert (D-57, R-180).
   *
   * Eén controle per criterium, en dat is de stilzwijgende keuze van ontwerp 7.4: het record
   * daar draagt één `machineUitkomst` en niet een lijst. Optioneel, want een criterium hoeft er
   * geen te hebben; dan is er niets machinaal vastgesteld en oordeelt de toetser zonder
   * voorwerk.
   *
   * **Een aangewezen controle beslist niet.** Wat ze oplevert staat naast het menselijke oordeel
   * en niet in de plaats ervan; R-149 is vervallen als instelling, dus er is geen keuze per
   * criterium tussen handmatig en machinaal. De catalogus staat in
   * `packages/engine/src/controles.ts`; hier staat alleen welke ervan is aangewezen, als naam en
   * niet als vorm, want de definitie hoort niet te weten hoe een controle rekent.
   */
  controle: z.string().min(1).optional(),
})

const repeatGroup = z.strictObject({
  ...labeled,
  control: z.literal('herhaalgroep'),
  /**
   * Per rij dezelfde veldset met dezelfde validatie per veld (R-40, R-41). Een
   * herhaalgroep in een herhaalgroep bestaat niet, en een component, criterium of
   * verwijzing in een rij ook niet: geen eis vraagt het en het maakt de opslag
   * per rij (een Y.Array van Y.Map, 6.8) onnodig diep.
   */
  rijVelden: z.array(RowField).min(1),
})

/**
 * De bron van een `verwijzing`, met het formaat van het verwezen veld uit het contract (R-54, R-55,
 * `docs/koppelvlak.md` hoofdstuk 2, besluit van 31 oktober 2026).
 *
 * **Bevroren bij publiceren, en nooit door de inrichter gezet**, om dezelfde reden als `rollen` bij
 * `SeriesSource`: de pod heeft geen contract en geen registry, en een blok buiten dit product heeft
 * geen weergave per bronveld. Zonder dit stond het budget van de call er als `750.000`; met dit weet
 * de pod dat het een bedrag in euro is (`freezeReferenceFormats` in
 * `packages/engine/src/referenceFormat.ts`). Het concept kent de sleutels niet (`SourceDraft`), en een
 * publicatie van vóór dit besluit draagt ze niet en toont wat ze altijd toonde.
 *
 * `valuta` hoort bij `format: amount` en staat er alleen dan, zoals in het contract.
 */
const VerwijzingSource = Source.extend({
  format: ContractFormat.optional(),
  valuta: ContractCurrency.optional(),
})

const verwijzing = z.strictObject({
  ...labeled,
  control: z.literal('verwijzing'),
  bron: VerwijzingSource,
})

/**
 * Het akkoord per persoon (besluit van 3 november 2026, eisenboek 12.56, ontwerp 6.2): een verklaring
 * die elke persoon met een toekenning als invuller op het dossier voor zichzelf geeft, naast het
 * akkoordveld van de indiener en niet in plaats ervan.
 *
 * **Een eigen vorm en geen modus op de toggle**, om de reden van de ontwerpstudie van die dag: het
 * akkoordveld is één waarde met de herkomst van één veld, en een tweede betekenis op hetzelfde veld
 * maakt de regel `akkoordJa`, het contract en de configurator dubbelzinnig.
 *
 * **Geen instellingen naast het label**, en dat is opzet. Wie akkoord kan geven staat niet in de definitie:
 * dat is ieder die de sectie mag bewerken, langs een toekenning als invuller of langs een deling met
 * bewerken, en geen rol uit de definitie (U6, R-67, R-91). Er is geen lijst van wie akkoord moet geven
 * (besluit van de product owner van 3 november 2026). Staat de regel `verplicht` erop, dan vraagt indienen
 * minstens één akkoord dat nog geldt; zonder die regel legt het veld alleen vast wie akkoord gaf. Het label
 * is de verklaring.
 */
const consentPerPerson = z.strictObject({
  ...labeled,
  control: z.literal('akkoordPerPersoon'),
})

const component = z.strictObject({
  ...core,
  control: z.literal('component'),
  component: z.enum(COMPONENTS),
  /** Een component brengt zijn eigen presentatie mee; een eigen kop is optioneel. */
  label: Text.optional(),
})

/** Alle 30 vormen die `control` kan aannemen. */
export const Field = z.discriminatedUnion('control', [
  ...SIMPLE,
  ...TOGGLE,
  ...SUBGRID,
  ...CHOICE,
  criterion,
  repeatGroup,
  verwijzing,
  component,
  consentPerPerson,
])
export type Field = z.infer<typeof Field>

/**
 * De controltypes waarvan de waarde een verzameling is. Daarop werkt de
 * regelsoort minimumAantal en maximumAantal (R-42): rijen bij een herhaalgroep,
 * trefwoorden bij een tag, gekozen opties bij een meerkeuzeveld of een
 * aanvinklijst.
 *
 * `file` staat er niet bij, en dat is opzet: voor een bijlageveld is
 * `bestandseisen` de aangewezen regel (R-104), met typen, aantal en grootte in
 * één. Twee regelsoorten die allebei het aantal bijlagen begrenzen zouden elkaar
 * kunnen tegenspreken.
 */
export const MULTI_VALUED = ['herhaalgroep', 'tag', 'multiselect', 'checkbox'] as const
export type MultiValued = (typeof MULTI_VALUED)[number]

/**
 * Welke antwoordvorm een controltype oplevert.
 *
 * De vier vormen van een antwoord staan in `revisie.ts` (`Waarde`, `Values`,
 * `Documents`, `Rows`). Welke vorm bij welk controltype hoort is kennis over de
 * vorm van een formulier en staat daarom hier, één keer: de renderer schrijft
 * ermee naar het sectiedocument (M2), de store bewaart ermee (M3) en de
 * configurator biedt ermee aan (M5). Een tweede kopie zou betekenen dat een
 * nieuw controltype op drie plekken moet worden bijgewerkt, en dat een vergeten
 * plek een antwoord in de verkeerde vorm oplevert.
 *
 * Vier vormen leveren geen antwoord in de stand op, en dat is elk om zijn eigen
 * reden:
 *
 * - `verwijzing` is een alleen-lezen waarde uit het dossier; niemand schrijft hem
 *   (R-56) en hij staat niet in het sectiedocument (ontwerp 6.8).
 * - `criterium` levert zijn oordeel en onderbouwing aan de laag en niet aan de
 *   antwoorden (R-152); dat is `CriteriumUitkomst` in `laag.ts`.
 * - `component` levert zijn gegevens gestructureerd aan het dossier (R-53), langs
 *   zijn eigen weg.
 * - `status`, `notification`, `progress`, `timer` en `subgrid` zijn weergave en geen
 *   invoer (ontwerp 6.2, categorie Weergave).
 *
 * `rating` staat wel op `waarde`, ook al is hij in de aangeboden schermen ongebruikt
 * en loopt de score via `slider` (ontwerp 6.2). Ongebruikt is niet hetzelfde als
 * zonder antwoord: wie hem plaatst krijgt een score, en die hoort in de stand.
 *
 * `richtext` staat op `waarde` en niet op een eigen vorm: in de stand komt rich
 * text als tekst binnen, leesbaar zonder Yjs (`revisie.ts`). Dat de opslag ervan
 * een `Y.XmlFragment` is en niet een sleutel in de waardenmap is een keuze van de
 * samenwerklaag (ontwerp 6.8) en geen eigenschap van het antwoord.
 *
 * Sinds R-50 draagt zo'n `Waarde` voor `richtext` daarnaast optioneel `structuur`: dezelfde
 * inhoud in ProseMirror/TipTap-vorm, naast de platte tekst. Dat is geen tweede antwoordvorm maar
 * een aanvulling op dezelfde: nodig omdat een atoomknoop (`figuur`, `referentie`) geen tekst
 * bijdraagt en anders stil verdwijnt bij een generatienaad (`apps/runtime/src/stand.ts`,
 * `generaties.ts`). Elk ander controltype laat dit veld leeg.
 */
export const RESPONSE_SHAPE = {
  text: 'waarde',
  textarea: 'waarde',
  number: 'waarde',
  currency: 'waarde',
  url: 'waarde',
  email: 'waarde',
  phone: 'waarde',
  richtext: 'waarde',
  dropdown: 'waarde',
  multiselect: 'waarden',
  toggle: 'waarde',
  checkbox: 'waarden',
  radio: 'waarde',
  date: 'waarde',
  datetime: 'waarde',
  lookup: 'waarde',
  tag: 'waarden',
  status: 'geen',
  notification: 'geen',
  rating: 'waarde',
  slider: 'waarde',
  progress: 'geen',
  file: 'documenten',
  image: 'documenten',
  timer: 'geen',
  subgrid: 'geen',
  criterium: 'laag',
  herhaalgroep: 'rijen',
  verwijzing: 'geen',
  component: 'geen',
  // Eén waarde, compleet of niet, met de akkoorden per persoon ernaast in het antwoord (`answer.ts`).
  akkoordPerPersoon: 'waarde',
} as const satisfies Record<Control, ResponseShape>

/**
 * `laag` is een uitkomst per criterium: oordeel, onderbouwing en de persoon die
 * oordeelde staan los van de invoer, haaks op de aanvraag (U23). `geen` is
 * weergave of een eigen weg naar het dossier.
 *
 * **Hier stond "de toetslaag", en dat was te krap** (M7.2). Een criterium in een
 * beoordelingsformulier staat in een gewone sectie (R-110) en dus in de invoerlaag;
 * R-152 en R-187 zijn een mechanisme. Wat `laag` zegt is dat de uitkomst naast de
 * antwoorden staat en niet ertussen, en niet in welke laag dat is.
 */
export type ResponseShape = 'waarde' | 'waarden' | 'documenten' | 'rijen' | 'laag' | 'geen'
